import React, { useEffect, useState, useRef } from "react";
import GridViewIcon from "@mui/icons-material/GridView";
import ReorderIcon from "@mui/icons-material/Reorder";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  Tab,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Grid,
  Typography,
  Tooltip,
  useMediaQuery,
  useTheme,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  CircularProgress,
} from "@mui/material";
import ArchiveIcon from "@mui/icons-material/Archive";
import StarIcon from "@mui/icons-material/Star";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import BackupIcon from "@mui/icons-material/Backup";
import SettingsIcon from "@mui/icons-material/Settings";
import CloudDoneIcon from "@mui/icons-material/CloudDone";
import { useDispatch, useSelector } from "react-redux";
import {
  getEvents,
  selectEvents,
  resetEvents,
  updateEventStatus,
  deleteEvent,
  deleteEventsWithoutTags,
  annotateEvents,
  removeAnnotations,
  // setEvents, // <-- Removed unused import
} from "./eventsSlice";
import { selectFilters, setFilterApplied } from "../filters/filterSlice";
import { Filters } from "../filters/Filters";
import { selectOrganization } from "../organization/organizationSlice";
import WeatherCard from "./WeatherCard";
import WeatherGrid from "./WeatherGrid";
import {
  BackupConfirmDialog,
  BackupDeleteConfirmDialog,
  BackupSettingsDialog,
} from "./BackupDialogs";
import { fetchBackupSettings, selectBackupRunning } from "./backupSlice";

export function EventsTable() {
  const [state, setState] = useState({ page: 0, rowsPerPage: 10 });
  const [selected, setSelected] = useState([]);
  const [selectMode, setSelectMode] = useState(false);
  const [listView, setListView] = useState(true);
  // const [reload, setReload] = useState(false); // <-- Removed unused state
  const [tab, setTab] = useState(0);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showAnnotateMenu, setShowAnnotateMenu] = useState(false);
  const [selectedAnnotations, setSelectedAnnotations] = useState([]);
  const [deleteOption, setDeleteOption] = useState("all");

  // Backup Dialogs State
  const [openBackup, setOpenBackup] = useState(false);
  const [openBackupDelete, setOpenBackupDelete] = useState(false);
  const [openSettings, setOpenSettings] = useState(false);

  const { species: allSpecies } = useSelector(selectOrganization);
  // const prevTab = useRef(tab); // <-- Removed unused ref
  const isFirstRender = useRef(true);
  const justRan = useRef(false);
  const { results: events, count } = useSelector(selectEvents);
  const filters = useSelector(selectFilters);
  const dispatch = useDispatch();
  const isFirstRun = useRef(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const initialLoadDone = useRef(false);

  const backupRunning = useSelector(selectBackupRunning);

  useEffect(() => {
    dispatch(fetchBackupSettings());
  }, [dispatch]);

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const isSuperUser = user?.username === "lumsadmin" || user?.is_staff === true;

  const getEventsWithTags = (eventIds) => {
    const selectedEvents = events.filter((event) =>
      eventIds.includes(event.uuid),
    );
    return selectedEvents.filter(
      (event) =>
        event.species &&
        event.species.some((s) => s.id === "fire" || s.id === "smoke"),
    );
  };

  const eventsWithTags = getEventsWithTags(selected);
  const eventsCountWithTags = eventsWithTags.length;
  const eventCountWithoutTags = selected.length - eventsCountWithTags;

  const status = (tab) => {
    switch (tab) {
      case 0:
        return "";
      case 1:
        return "ARCHIVED";
      case 2:
        return "FEATURED";
      default:
        return "NONE";
    }
  };

  const { page, rowsPerPage } = state;

  // Initial Load
  useEffect(() => {
    if (!initialLoadDone.current) {
      initialLoadDone.current = true;
      dispatch(getEvents(1, filters.filterApplied, status(tab), rowsPerPage));
    }
  }, []); // eslint-disable-line

  // Handle rowsPerPage changes
  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }
    setSelected([]);
    dispatch(resetEvents());
    setState((prev) => ({ ...prev, page: 0 }));
    dispatch(getEvents(1, filters.filterApplied, status(tab), rowsPerPage));
  }, [rowsPerPage]); // eslint-disable-line

  // Handle filter changes
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (justRan.current) {
      justRan.current = false;
      return;
    }
    dispatch(
      getEvents(
        state.page + 1,
        filters.filterApplied,
        status(tab),
        rowsPerPage,
      ),
    );
    justRan.current = true;
    let check = filters.filterApplied ? false : filters.filterApplied;
    dispatch(setFilterApplied(check));
  }, [filters]); // eslint-disable-line

  // Handle tab changes
  useEffect(() => {
    if (initialLoadDone.current) {
      dispatch(resetEvents());
      setState({ page: 0, rowsPerPage });
      dispatch(getEvents(1, filters.filterApplied, status(tab), rowsPerPage));
    }
  }, [tab]); // eslint-disable-line

  // Reset selection when exiting select mode
  useEffect(() => {
    if (selectMode === false) setSelected([]);
  }, [selectMode]);

  const showChanges = async () => {
    await new Promise((resolve) => setTimeout(resolve, 1000));
    dispatch(getEvents(1, true, status(tab), rowsPerPage));
  };

  const handleChangePage = (event, newPage) => {
    // Always fetch the next page from the backend
    dispatch(
      getEvents(newPage + 1, filters.filterApplied, status(tab), rowsPerPage),
    );
    setState({ page: newPage, rowsPerPage });
    setSelected([]);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setState({ page: 0, rowsPerPage: newRowsPerPage });
    // Fetch page 1 with new page size
    dispatch(getEvents(1, filters.filterApplied, status(tab), newRowsPerPage));
    setSelected([]);
  };

  const handleArchive = () => {
    if (tab !== 1) dispatch(updateEventStatus(selected, "archive"));
    else dispatch(updateEventStatus(selected, "restore"));
    setSelected([]);
    if (tab === 2 || tab === 1) showChanges();
  };

  const handleStar = () => {
    if (tab !== 2) dispatch(updateEventStatus(selected, "feature"));
    else dispatch(updateEventStatus(selected, "restore"));
    setSelected([]);
    if (tab === 2 || tab === 1) showChanges();
  };

  const handleDelete = () => {
    if (deleteOption === "all") dispatch(deleteEvent(selected));
    else dispatch(deleteEventsWithoutTags(selected));
    setShowDeleteConfirmation(false);
    setSelected([]);
    setDeleteOption("all");
    showChanges();
  };

  const handleAnnotate = () => {
    dispatch(annotateEvents(selected, selectedAnnotations));
    setShowAnnotateMenu(false);
    setSelected([]);
    showChanges();
  };

  const handleUnAnnotate = () => {
    dispatch(removeAnnotations(selected, selectedAnnotations));
    setShowAnnotateMenu(false);
    setSelected([]);
    showChanges();
  };

  return (
    <Paper className="mb4" sx={{ p: 0, m: 0, overflow: "hidden" }}>
      {/* Delete Dialog */}
      <Dialog
        open={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: "#f44336", color: "white" }}>
          Delete Events
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <DialogContentText sx={{ mb: 2 }}>
            You have selected <strong>{selected.length}</strong> event(s) to
            delete.
          </DialogContentText>
          {isSuperUser ? (
            <FormControl component="fieldset" sx={{ mt: 2, width: "100%" }}>
              <FormLabel component="legend">Select deletion option:</FormLabel>
              <RadioGroup
                value={deleteOption}
                onChange={(e) => setDeleteOption(e.target.value)}
              >
                <FormControlLabel
                  value="all"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                        🗑️ Delete all selected events
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="withoutTags"
                  control={<Radio />}
                  disabled={eventCountWithoutTags === 0}
                  label={
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                        🔒 Delete events without tags only
                      </Typography>
                    </Box>
                  }
                />
              </RadioGroup>
            </FormControl>
          ) : (
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{ mt: 2, p: 2, bgcolor: "#f5f5f5", borderRadius: 1 }}
            >
              ℹ️ You can only delete events without Fire/Smoke tags.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: "space-between" }}>
          <Button
            onClick={() => setShowDeleteConfirmation(false)}
            sx={{ color: "#666" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            sx={{ bgcolor: deleteOption === "all" ? "#f44336" : "#4caf50" }}
            disabled={!isSuperUser && eventCountWithoutTags === 0}
          >
            {deleteOption === "all"
              ? `Delete All ${selected.length} Event(s)`
              : `Delete ${eventCountWithoutTags} Event(s)`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Annotate Dialog */}
      <Dialog
        open={showAnnotateMenu}
        onClose={() => setShowAnnotateMenu(false)}
      >
        <DialogTitle>Annotate Event(s)</DialogTitle>
        <DialogContent dividers>
          <DialogContentText>
            Select all the labels you want to apply (or remove)!
          </DialogContentText>
          <FormGroup>
            {allSpecies.map((species) => (
              <FormControlLabel
                control={<Checkbox name={species.name} />}
                label={species.name}
                key={species.name}
                onChange={(e) => {
                  if (e.target.checked)
                    setSelectedAnnotations([
                      ...selectedAnnotations,
                      species.name,
                    ]);
                  else
                    setSelectedAnnotations(
                      selectedAnnotations.filter(
                        (item) => item !== species.name,
                      ),
                    );
                }}
              />
            ))}
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowAnnotateMenu(false);
              setSelectedAnnotations([]);
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleUnAnnotate}>UnAnnotate</Button>
          <Button onClick={handleAnnotate} autoFocus>
            Annotate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Backup Dialogs */}
      <BackupConfirmDialog
        open={openBackup}
        onClose={() => setOpenBackup(false)}
      />
      <BackupDeleteConfirmDialog
        open={openBackupDelete}
        onClose={() => setOpenBackupDelete(false)}
      />
      <BackupSettingsDialog
        open={openSettings}
        onClose={() => setOpenSettings(false)}
      />

      {/* Toolbar */}
      <Box
        sx={{
          borderBottom: "1px solid",
          borderColor: "divider",
          position: "sticky",
          top: 0,
          background:
            "linear-gradient(135deg, #a9a9a9 0%, rgba(40, 39, 39, 0.95) 100%)",
          zIndex: 2,
          px: 0,
        }}
      >
        <Tabs
          value={tab}
          onChange={(e, v) => setTab(v)}
          variant={isMobile ? "scrollable" : "standard"}
          scrollButtons={isMobile ? "auto" : false}
          allowScrollButtonsMobile={isMobile}
          sx={{
            "& .MuiTab-root": {
              fontSize: isMobile ? "0.7rem" : "0.875rem",
              minWidth: isMobile ? "auto" : 90,
              px: isMobile ? 1 : 2,
              color: "white",
            },
          }}
        >
          <Tab label="All" /> <Tab label="Archived" /> <Tab label="Featured" />
        </Tabs>

        <Box
          sx={{
            position: "absolute",
            right: 15,
            top: 8,
            display: "flex",
            alignItems: "center",
            gap: isMobile ? 0.5 : 1,
          }}
        >
          <Filters />
          <Tooltip title="Backup only (no delete)">
            <span>
              <Button
                onClick={() => setOpenBackup(true)}
                disabled={backupRunning}
                startIcon={
                  backupRunning ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <BackupIcon />
                  )
                }
                sx={{
                  color: "white",
                  textTransform: "none",
                  borderRadius: "30px",
                  border: "1px solid rgba(255,255,255,0.4)",
                  px: 2,
                  py: 0.5,
                  fontSize: isMobile ? "0.7rem" : "0.875rem",
                  display: { xs: "none", sm: "flex" },
                  "&:hover": { backgroundColor: "rgba(255,255,255,0.2)" },
                }}
              >
                {isMobile ? "" : "Backup"}
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Backup + Delete untagged">
            <span>
              <Button
                onClick={() => setOpenBackupDelete(true)}
                disabled={backupRunning}
                startIcon={
                  backupRunning ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <DeleteSweepIcon />
                  )
                }
                sx={{
                  color: "#ff8a80",
                  textTransform: "none",
                  borderRadius: "30px",
                  border: "1px solid #ff8a80",
                  px: 2,
                  py: 0.5,
                  fontSize: isMobile ? "0.7rem" : "0.875rem",
                  display: { xs: "none", sm: "flex" },
                  "&:hover": { backgroundColor: "rgba(255,82,82,0.2)" },
                }}
              >
                {isMobile ? "" : "Backup & Delete"}
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Auto-Backup Settings">
            <Button
              onClick={() => setOpenSettings(true)}
              disabled={backupRunning}
              sx={{
                color: "white",
                minWidth: "auto",
                px: 1,
                py: 0.5,
                borderRadius: "30px",
              }}
            >
              <SettingsIcon fontSize="small" />
            </Button>
          </Tooltip>

          <Button
            onClick={() => setSelectMode(!selectMode)}
            variant="text"
            size="small"
            sx={{
              border: "white",
              minWidth: isMobile ? "auto" : "auto",
              px: isMobile ? 1 : 2,
              py: isMobile ? 0.5 : 0.5,
              borderRadius: "30px",
              color: "white",
              fontSize: isMobile ? "0.7rem" : "0.875rem",
              fontWeight: 500,
              textTransform: "none",
              "&:hover": {
                backgroundColor: "white",
                color: "grey",
                transform: "translateY(-2px)",
                transition: "all 0.3s ease",
              },
            }}
          >
            Select
          </Button>

          {listView ? (
            <Tooltip title="Grid View">
              <GridViewIcon
                sx={{
                  color: "white",
                  cursor: "pointer",
                  fontSize: isMobile ? 20 : 24,
                  "&:hover": { opacity: 0.7 },
                }}
                onClick={() => setListView(!listView)}
              />
            </Tooltip>
          ) : (
            <Tooltip title="List view">
              <ReorderIcon
                sx={{
                  color: "white",
                  cursor: "pointer",
                  fontSize: isMobile ? 20 : 24,
                  "&:hover": { opacity: 0.7 },
                }}
                onClick={() => setListView(!listView)}
              />
            </Tooltip>
          )}
        </Box>

        {backupRunning && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 2,
              py: 1,
              bgcolor: "rgba(255,255,255,0.15)",
              borderTop: "1px solid rgba(255,255,255,0.1)",
            }}
          >
            <CircularProgress size={16} sx={{ color: "white" }} />
            <Typography variant="body2" sx={{ color: "white", fontSize: 13 }}>
              Backup running in background. You can continue working.
            </Typography>
          </Box>
        )}

        {selected.length > 0 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: isMobile ? 1 : 2,
              py: isMobile ? 1 : 1,
              bgcolor: "#f5f5f5",
              borderTop: "1px solid #e0e0e0",
              flexDirection: isMobile ? "column" : "row",
              gap: isMobile ? 1 : 0,
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {selected.length} selected
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: isMobile ? 1 : 2,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <Button
                size="small"
                onClick={() => {
                  setShowAnnotateMenu(true);
                  setSelectedAnnotations([]);
                }}
                sx={{ fontSize: isMobile ? "0.7rem" : "0.875rem" }}
              >
                Annotations Menu
              </Button>
              <Box sx={{ display: "flex", gap: 1 }}>
                <div onClick={handleArchive} style={{ cursor: "pointer" }}>
                  {tab === 1 ? (
                    <UnarchiveIcon
                      sx={{ color: "red", fontSize: isMobile ? 20 : 24 }}
                    />
                  ) : (
                    <ArchiveIcon sx={{ fontSize: isMobile ? 20 : 24 }} />
                  )}
                </div>
                <StarIcon
                  onClick={handleStar}
                  sx={{
                    color: tab === 2 ? "red" : "black",
                    cursor: "pointer",
                    fontSize: isMobile ? 20 : 24,
                  }}
                />
                <DeleteIcon
                  onClick={() => setShowDeleteConfirmation(true)}
                  sx={{
                    color: "red",
                    cursor: "pointer",
                    fontSize: isMobile ? 20 : 24,
                  }}
                />
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      {/* Table/Grid Content */}
      <Box
        sx={{
          p: isMobile ? (listView ? 0 : 1) : listView ? 2 : 2,
          minHeight: 400,
        }}
      >
        {listView ? (
          <TableContainer
            component={Paper}
            sx={{
              boxShadow: "none",
              border: "1px solid #e0e0e0",
              overflowX: "auto",
            }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {selectMode && (
                    <TableCell padding="checkbox" sx={{ width: 40 }}>
                      <Checkbox
                        checked={
                          events.length > 0 &&
                          events.every((event) => selected.includes(event.uuid))
                        }
                        onChange={() => {
                          const allSelected = events.every((event) =>
                            selected.includes(event.uuid),
                          );
                          if (allSelected)
                            setSelected(
                              selected.filter(
                                (id) => !events.some((e) => e.uuid === id),
                              ),
                            );
                          else
                            setSelected([
                              ...selected,
                              ...events
                                .map((e) => e.uuid)
                                .filter((id) => !selected.includes(id)),
                            ]);
                        }}
                      />
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {events.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={selectMode ? 2 : 1} align="center">
                      <Typography sx={{ py: 4, color: "#999" }}>
                        No events found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  events.map((row) => (
                    <TableRow key={row.uuid} hover>
                      {selectMode && (
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selected.includes(row.uuid)}
                            onChange={() => {
                              if (selected.includes(row.uuid))
                                setSelected(
                                  selected.filter((id) => id !== row.uuid),
                                );
                              else setSelected([...selected, row.uuid]);
                            }}
                          />
                        </TableCell>
                      )}
                      <TableCell sx={{ pr: 5, position: "relative" }}>
                        {row.is_backed_up && (
                          <Tooltip
                            title={`Backed up on ${new Date(row.backup_date).toLocaleString()}`}
                          >
                            <CloudDoneIcon
                              sx={{
                                position: "absolute",
                                top: 10,
                                right: 10,
                                color: "#4caf50",
                                zIndex: 10,
                                fontSize: 24,
                                filter:
                                  "drop-shadow(0px 2px 4px rgba(0,0,0,0.3))",
                              }}
                            />
                          </Tooltip>
                        )}
                        <WeatherCard data={row} />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <div>
            {selectMode ? (
              <span style={{ display: "inline-flex", alignItems: "center" }}>
                <Checkbox
                  checked={
                    events.length > 0 &&
                    events.every((event) => selected.includes(event.uuid))
                  }
                  onChange={() => {
                    const allSelected = events.every((event) =>
                      selected.includes(event.uuid),
                    );
                    if (allSelected)
                      setSelected(
                        selected.filter(
                          (id) => !events.some((e) => e.uuid === id),
                        ),
                      );
                    else
                      setSelected([
                        ...selected,
                        ...events
                          .map((e) => e.uuid)
                          .filter((id) => !selected.includes(id)),
                      ]);
                  }}
                />
                <span>
                  <h4>Select All</h4>
                </span>
              </span>
            ) : null}
            <Grid
              container
              spacing={isMobile ? 1 : 2}
              style={{ justifyContent: "center" }}
            >
              {events.map((event) => (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  lg={2.4}
                  key={event.uuid}
                  sx={{ display: "flex", justifyContent: "center" }}
                >
                  <div
                    className="card rounded my-3 shadow-lg back-card"
                    style={{
                      width: isMobile ? "calc(100% - 10px)" : "235px",
                      maxWidth: "235px",
                      margin: "5px",
                      height: "fit-content",
                      maxHeight: "460px",
                      position: "relative",
                    }}
                    key={event.uuid}
                  >
                    {event.is_backed_up && (
                      <Tooltip
                        title={`Backed up on ${new Date(event.backup_date).toLocaleString()}`}
                      >
                        <CloudDoneIcon
                          sx={{
                            position: "absolute",
                            top: 45,
                            right: 2,
                            color: "#4caf50",
                            zIndex: 10,
                            fontSize: 24,
                            filter: "drop-shadow(0px 2px 4px rgba(0,0,0,0.3))",
                          }}
                        />
                      </Tooltip>
                    )}
                    <WeatherGrid data={event} />
                  </div>
                </Grid>
              ))}
            </Grid>
          </div>
        )}
      </Box>

      <TablePagination
        rowsPerPageOptions={[10, 20, 50, 100]}
        component="div"
        count={count !== null ? count : 0}
        rowsPerPage={rowsPerPage}
        page={page}
        onRowsPerPageChange={handleChangeRowsPerPage}
        onPageChange={handleChangePage}
        showFirstButton
        sx={{
          "& .MuiTablePagination-toolbar": {
            flexWrap: isMobile ? "wrap" : "nowrap",
            gap: isMobile ? 1 : 0,
            minHeight: isMobile ? "auto" : "auto",
            padding: isMobile ? "8px 16px" : "0 16px",
          },
          "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-select":
            { fontSize: isMobile ? "0.75rem" : "0.875rem" },
        }}
      />
    </Paper>
  );
}
